import { Link } from "react-router-dom";
import { Mail, Phone, MapPin } from "lucide-react";

const Footer = () => {
  return (
    <footer className="bg-[#1A1A1A] text-white py-16" data-testid="main-footer">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
        {/* Logo at top of footer */}
        <div className="flex justify-center mb-8">
          <img 
            src="https://customer-assets.emergentagent.com/job_legal-advisory-pro/artifacts/j8sybs2h_rera%20hub.jpeg" 
            alt="RERA Hub Logo" 
            className="footer-logo h-10 md:h-12 w-auto transition-opacity duration-300 hover:opacity-80"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-12">
          {/* Company Info */}
          <div>
            <h3 className="text-lg font-semibold mb-4" data-testid="footer-about-title">
              About Us
            </h3>
            <p className="text-slate-300 leading-relaxed text-sm">
              Professional RERA registration, compliance and advisory services for your real estate business needs.
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-lg font-semibold mb-4" data-testid="footer-quick-links-title">
              Quick Links
            </h4>
            <ul className="space-y-2">
              <li>
                <Link
                  to="/"
                  className="text-slate-300 hover:text-[#C9A14A] transition-colors duration-300 text-sm"
                  data-testid="footer-link-home"
                >
                  Home
                </Link>
              </li>
              <li>
                <Link
                  to="/about"
                  className="text-slate-300 hover:text-[#C9A14A] transition-colors duration-300 text-sm"
                  data-testid="footer-link-about"
                >
                  About Us
                </Link>
              </li>
              <li>
                <Link
                  to="/services"
                  className="text-slate-300 hover:text-[#C9A14A] transition-colors duration-300 text-sm"
                  data-testid="footer-link-services"
                >
                  Services
                </Link>
              </li>
              <li>
                <Link
                  to="/contact"
                  className="text-slate-300 hover:text-[#C9A14A] transition-colors duration-300 text-sm"
                  data-testid="footer-link-contact"
                >
                  Contact
                </Link>
              </li>
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="text-lg font-semibold mb-4" data-testid="footer-services-title">
              Our Services
            </h4>
            <ul className="space-y-2 text-slate-300 text-sm">
              <li>RERA Registration</li>
              <li>Extension Application</li>
              <li>Correction Application</li>
              <li>De-registration</li>
              <li>Project Closure / Bank Account Closure</li>
              <li>Legal Compliance</li>
              <li>Documentation Services</li>
              <li>Business Advisory</li>
              <li>QPR & APR Compliances</li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-lg font-semibold mb-4" data-testid="footer-contact-title">
              Contact Us
            </h4>
            
            {/* Nashik Office */}
            <div className="mb-4">
              <div className="font-semibold text-white mb-2">Nashik Office</div>
              <ul className="space-y-2">
                <li className="flex items-start gap-3">
                  <MapPin size={16} className="mt-1 text-[#C9A14A] flex-shrink-0" />
                  <div className="text-slate-300 text-sm">
                    Office No 2, Shri Balaji Heights, Ravi Shankar Marg, Kalpataru Nagar, Nashik - 422011
                  </div>
                </li>
                <li className="flex items-center gap-3">
                  <Phone size={16} className="text-[#C9A14A] flex-shrink-0" />
                  <a
                    href="tel:+917758882796"
                    className="text-slate-300 hover:text-[#C9A14A] transition-colors duration-300 text-sm"
                  >
                    +91-77588 82796
                  </a>
                </li>
              </ul>
            </div>

            {/* Pune Office */}
            <div className="mb-4">
              <div className="font-semibold text-white mb-2">Pune Office</div>
              <ul className="space-y-2">
                <li className="flex items-start gap-3">
                  <MapPin size={16} className="mt-1 text-[#C9A14A] flex-shrink-0" />
                  <div className="text-slate-300 text-sm">
                    Office No 1002/26, 2nd floor, Saakar building, opp. Sachin Tendulkar park, Navi peth, Pune - 411030
                  </div>
                </li>
                <li className="flex items-center gap-3">
                  <Phone size={16} className="text-[#C9A14A] flex-shrink-0" />
                  <a
                    href="tel:+919404515431"
                    className="text-slate-300 hover:text-[#C9A14A] transition-colors duration-300 text-sm"
                  >
                    +91-9404515431
                  </a>
                </li>
              </ul>
            </div>

            {/* Email */}
            <div className="flex items-center gap-3 mb-4">
              <Mail size={16} className="text-[#C9A14A]" />
              <a
                href="mailto:hub.rera@gmail.com"
                className="text-slate-300 hover:text-[#C9A14A] transition-colors duration-300 text-sm"
                data-testid="footer-email-link"
              >
                hub.rera@gmail.com
              </a>
            </div>

            {/* Social Media */}
            <div className="flex gap-4 mt-6">
              <a
                href="https://www.facebook.com/share/1B7ZoBtwS3"
                target="_blank"
                rel="noopener noreferrer"
                className="text-slate-300 hover:text-[#C9A14A] transition-colors duration-300"
                data-testid="footer-social-facebook"
                aria-label="Facebook"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                </svg>
              </a>
              <a
                href="https://www.instagram.com/rerahub?igsh=cWx0ejhrM2txMGpn"
                target="_blank"
                rel="noopener noreferrer"
                className="text-slate-300 hover:text-[#C9A14A] transition-colors duration-300"
                data-testid="footer-social-instagram"
                aria-label="Instagram"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                </svg>
              </a>
              <a
                href="https://www.linkedin.com/company/rerahub"
                target="_blank"
                rel="noopener noreferrer"
                className="text-slate-300 hover:text-[#C9A14A] transition-colors duration-300"
                data-testid="footer-social-linkedin"
                aria-label="LinkedIn"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                </svg>
              </a>
            </div>
          </div>
        </div>

        <div className="border-t border-slate-700 mt-12 pt-8 text-center text-slate-400">
          <p data-testid="footer-copyright" className="text-sm">
            &copy; {new Date().getFullYear()} RERA Hub. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
